import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { useLanguage } from "./contexts/language-state/LanguageContext";
import { useStatement } from "./contexts/StatementContext";
import { getQuestionAndLanguageOnID, getQuestionTypeOnQuestionID } from "../data-sources/api";
import { VideoPlayer } from "./players/VideoPlayer";
import { AudioPlayer } from "./players/AudioPlayer";
import { YesNo } from "./answer-components/YesNo";
import { CountrySelect } from "./answer-components/CountrySelect";
import { LanguageSelect } from "./answer-components/LanguageSelect";
import { MultiButtons } from "./answer-components/MultiButtons";

/**
 * StatementDetails component that fetches and displays details of a statement based on the selected language and statement ID.
 * 
 * @returns {JSX.Element} The rendered statement details with appropriate input components.
 */
export function StatementDetails() {
  // Set contexts
  const { language } = useLanguage();
  const { statementID } = useStatement();

  // Set states
  const [statement, setStatement] = useState({});
  const [statementType, setStatementType] = useState();
  const [startDate, setStartDate] = useState(new Date());
  const [audioHasPlayed, setAudioHasPlayed] = useState(false);

  // Set refs
  const statementSection = useRef(null);

  /**
   * Fetches statement data and statement type based on the statement ID and language ID.
   */
  const fetchData = async () => {
    try {
      const [statementRes, statementTypeRes] = await Promise.all([
        getQuestionAndLanguageOnID(statementID, language.id),
        getQuestionTypeOnQuestionID(statementID)
      ]);

      setStatement(statementRes.data[0]);
      setStatementType(statementTypeRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  /**
   * Handles the end of audio playback
   */
  const handleEndedAudio = () => {
    setAudioHasPlayed(true);
  }

  /**
   * Fetch data and scroll to the statement section when statement or language changes
   */
  useEffect(() => {
    if (statementID && language.id) {
      setAudioHasPlayed(false);
      fetchData();

      window.scrollTo({ top: statementSection.current.offsetTop, behavior: "smooth" })
    }
  }, [statementID, language]);

  return (
    <div className="content-container" ref={statementSection}>
      {statementID &&
        <>
          <h2 className="content-header medium-text">{statement.textUrl}</h2>

          <div className="content-frame">
            { // If the statement's main content is not a video, then it contains an audio
              statementType != "PXQVideo" && <AudioPlayer url={statement.audioUrl} onEndedAudio={handleEndedAudio} />
            }

            { // If the statement's main content is a video
              statementType == "PXQVideo" && <VideoPlayer url={statement.videoUrl} />
            }
          </div>

          {audioHasPlayed &&
            <div className="answer-frame light-text">
              { // If the question should be answered with plain text
                (statementType == "PXQLetters" || statementType == "PXQNumbers" || statementType == "PXQYears") &&
                <Form.Control type="text" className="input-field" />
              }

              { // If the question should be answered with yes or no answers
                statementType == "PXQYesNo" && <YesNo languageID={language.id} />
              }

              { // If the question should be answered with a date
                statementType === "PXQCalendar" && (
                  <DatePicker
                    selected={startDate}
                    dateFormat="yyyy-MM-dd"
                    onChange={date => setStartDate(date)}
                  />
                )
              }

              { // If the question should be answered with a country
                statementType == "PXQCountries" && <CountrySelect languageID={language.id} />
              }

              { // If the question should be answered with a language
                statementType == "PXQLanguages" && <LanguageSelect languageID={language.id} />
              }

              { // If the question should be answered with yes or no answer, and then with a country
                statementType === "PXQYesNoCountries" && (
                  <>
                    <YesNo languageID={language.id} />
                    <CountrySelect languageID={language.id} />
                  </>
                )
              }

              { // If the question should be answered with multi choice buttons
                statementType == "PXQMultiButton" && <MultiButtons statementID={statementID} languageID={language.id} selectMultiple={false} />
              }

              { // If the question should be answered with multi select buttons, i.e. multi choice buttons where multiple choices can be selected
                statementType == "PXQMultiSelect" && <MultiButtons statementID={statementID} languageID={language.id} selectMultiple={true} />
              }

              { // If the question should be answered with a yes or no answer, then with multi choice buttons
                statementType == "PXQYesNoMulti" && (
                  <>
                    <YesNo languageID={language.id} />
                    <MultiButtons statementID={statementID} languageID={language.id} selectMultiple={false} />
                  </>
                )
              }
            </div>
          }
        </>}
    </div>
  );
}